import React, { useContext, useEffect, useState } from "react";
import { FSidebar } from "../components/FSidebar";
import { useLocation, useNavigate } from "react-router-dom";
import { LayoutContext, UserContext } from "@contexts/index";
import MobileNavBar from "@components/mobileComponents/MobileNavBar";
import MobileSidebar from "@components/mobileComponents/MobileSidebar";

import { Permission, Role } from "@constants/roles";
import { LOCAL_API_ORIGIN } from "@constants/api";

import Toaster from "@components/Toaster";
import { isEmpty } from "lodash";
import ImpersonationBanner from "@components/ImpersonationBanner";
import { MixpanelContext } from "@hooks/MixpanelProvider";
import { useBreakpoint } from "@hooks/tailwind";
import { getSharedLink } from "@utils/shared-links";

export default function MainLayout({ children }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const location = useLocation();
  const { trackEvent } = useContext(MixpanelContext);

  const navigate = useNavigate();

  const { can, hasRole, user } = useContext(UserContext);

  const isInfluencer = hasRole(Role.INFLUENCER);

  useEffect(() => {
    const sharedLink = getSharedLink(user);

    if (sharedLink) {
      navigate(sharedLink);
    }
  }, [user]);

  const isFullScreenRoute = [
    "/card/",
    "/card-new/",
    "/brief-preview/",
    "/participants/",
    "/campaign-view/",
    "/brief/",
    "/bulletins/",
    ...(isInfluencer ? ["/home"] : []),
  ].some((path) => location.pathname.startsWith(path));

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const isMobileView = !useBreakpoint("c-lg");

  const { brand_id, memberships } = user;

  const ambassadorSection = () => {
    const { email } = user;

    const items = [
      can(Permission.VIEW_AMBASSADORS_ACCEPTED) && {
        icon: "audience",
        name: "Collaborators",
        href: "/collaborators",
      },
      can(Permission.VIEW_AMBASSADORS_APPLICANTS) && {
        icon: "content",
        name: "Ambassadors",
        href: "/ambassadors",
        isSubmenuItem: true,
        isDefault: email && !email.endsWith("fohr.co"),
      },
    ].filter(Boolean);

    if (isEmpty(items)) {
      return null;
    }

    return {
      fixedBottom: false,
      collapsible: true,
      icon: "crown",
      title: "Community",
      items,
    };
  };

  const currentMembership = memberships.find(
    (membership) => membership.brand_id === brand_id
  );

  const { role } = currentMembership || {};

  const settingsSection = {
    fixedBottom: true,
    collapsible: false,
    items: [
      [Role.FOHR_ADMIN, Role.SUPER_ADMIN].includes(role as Role)
        ? { name: "Admin", href: "/admin", icon: "influencers" }
        : null,
      user.pixel
        ? {
            name: "Pixel",
            href: `${LOCAL_API_ORIGIN}/api/v1/pixel/auth`,
            icon: "globe",
          }
        : null,
    ].filter(Boolean),
  };

  // Define sections for both desktop and mobile
  const sidebarSections = [
    {
      fixedBottom: false,
      items: [
        { name: "Home", href: "/home", icon: "home" },
        !hasRole(Role.INFLUENCER)
          ? { icon: "search", name: "Search", href: "/search" }
          : null,
        can(Permission.VIEW_CAMPAIGNS) || hasRole(Role.INFLUENCER)
          ? {
              icon: "bookmark",
              name: "Campaigns",
              href: "/campaigns",
            }
          : null,
      ].filter(Boolean),
    },
    ambassadorSection(),
    {
      fixedBottom: false,
      items: [
        {
          icon: "gifts",
          name: "Bulletins",
          href: "/bulletins",
        },
        can(Permission.VIEW_GROUPS) && {
          icon: "bulletins",
          name: "Groups",
          href: "/groups",
        },

        can(Permission.VIEW_OUTREACH)
          ? { icon: "email", name: "Outreach", href: "/outreach" }
          : null,
        can(Permission.VIEW_REPORTS)
          ? {
              icon: "graph",
              name: "Reports",
              href: `/reports`,
            }
          : null,
        !hasRole(Role.INFLUENCER)
          ? {
              icon: "chat",
              name: "Notifications",
              href: `/notifications`,
            }
          : null,
      ].filter(Boolean),
    },
    settingsSection,
  ].filter(Boolean);

  const isOpenLink = hasRole(Role.OPEN_LINK);

  return (
    <div className="bg-gradient-to-b from-highlight_red to-light_red">
      <ImpersonationBanner></ImpersonationBanner>
      <Toaster />
      <LayoutContext.Provider
        value={{
          collapsed: isCollapsed,
          setCollapsed: setIsCollapsed,
          fullNavWidth: "244px",
          collapsedNavWidth: isOpenLink ? "0px" : "66px",
        }}
      >
        {/* Mobile Navigation Bar (top) */}
        {(isMobileView || isOpenLink) && (
          <div className="sticky top-0 z-[500]">
            <MobileNavBar
              user={user}
              isOpenLink={isOpenLink}
              toggleSidebar={toggleMobileSidebar}
              isMenuOpen={isMobileSidebarOpen}
              trackMenuEvent={(eventName, properties) => {
                trackEvent(`USER_MENU.${eventName}`, properties);
              }}
            />
          </div>
        )}

        {/* Mobile Sidebar (slide-in) */}
        <MobileSidebar
          isOpen={isMobileSidebarOpen}
          onClose={() => setIsMobileSidebarOpen(false)}
          sections={sidebarSections}
        />

        <div className="flex relative max-w-full overscroll-x-auto overscroll-none">
          {/* Desktop Sidebar - hidden on mobile */}
          {!isMobileView && !isOpenLink && (
            <FSidebar
              className={`static sm:sticky top-0 self-start z-[100] relative`}
              onToggleCollapsed={setIsCollapsed}
              isCollapsed={isCollapsed}
              sections={sidebarSections}
            />
          )}

          <div
            className={`w-full overscroll-none c-lg:shadow-xl ${isOpenLink ? "" : "c-lg:rounded-l-2xl"} z-[200] border-l min-h-screen border-highlight_red bg-light_red ${
              !isMobileView
                ? isCollapsed
                  ? "max-w-[calc(100vw-66px)]"
                  : isOpenLink
                    ? "100vw"
                    : "max-w-[calc(100vw-244px)]"
                : "max-w-full"
            }`}
          >
            {/*  {user.payment_current === false &&
              !user.email?.endsWith("fohr.co") && (
                <div className="bg-black text-white text-center h-[28px] gap-2 text-xs justify-center items-center flex">
                  <span>
                    You're on the free plan. Upgrade to gain full access to all
                    features.
                  </span>{" "}
                  <Link to="/settings/subscription" className="underline">
                    Subscribe here
                  </Link>
                </div>
              )} */}
            <div
              className={`w-full overscroll-none min-h-screen ${
                isFullScreenRoute ? "" : "px-[15px] sm:px-[60px] mt-[40px] z-10"
              }`}
            >
              {children}
            </div>
          </div>
        </div>
      </LayoutContext.Provider>
    </div>
  );
}
